/**
 * Metering slice (PX-W6.4) — the AI-run usage projection (one shape at a
 * time) and the run-budget remainder of the opened project.
 *
 * The usage cache is keyed `projectId:what` — switching the shape tab is a
 * context change, which the standard `fetchCache` discipline treats as a
 * fresh fetch. A 404 on the run budget lands as `remainingMissing` ("no run
 * budget agreed", I-7 — never zero); `remainingMinor` may be negative and is
 * stored verbatim (an overrun is the number a delivery lead wants).
 */

import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'

import { isApiError, toApiError } from '@/services'
import {
  fetchFailed,
  fetchStarted,
  fetchSucceeded,
  initialFetchCache,
  invalidate,
  shouldFetch,
  type FetchCache
} from '@/store/fetchCache'
import { meteringServiceHttp } from '../services/meteringService.http'
import type { MeteringUsageShape, RemainingBudget, UsageSummary } from '../types'

/** A failed thunk's payload — key, message, and the HTTP status (the 404 branch reads it). */
export interface MeteringError {
  key?: string
  status?: number
  message: string
}

export interface MeteringState {
  usage: UsageSummary | null
  /** The shape the loaded usage was requested for. */
  usageShape: MeteringUsageShape
  remaining: RemainingBudget | null
  /** True when the run-budget GET answered 404 — "no run budget agreed". */
  remainingMissing: boolean
  caches: { usage: FetchCache; remaining: FetchCache }
  error: MeteringError | null
}

const initialState: MeteringState = {
  usage: null,
  usageShape: 'summary',
  remaining: null,
  remainingMissing: false,
  caches: {
    usage: { ...initialFetchCache },
    remaining: { ...initialFetchCache }
  },
  error: null
}

const toMeteringError = (error: unknown): MeteringError => {
  const apiError = isApiError(error) ? error : toApiError(error)
  return { key: apiError.backendKey, status: apiError.status, message: apiError.message }
}

const usageKey = (projectId: string, what: MeteringUsageShape): string => `${projectId}:${what}`

// ---------------------------------------------------------------------------
// Thunks
// ---------------------------------------------------------------------------

/** Load the usage projection for one shape — keyed `projectId:what`. */
export const fetchMeteringUsage = createAsyncThunk<
  UsageSummary,
  { projectId: string; what: MeteringUsageShape },
  { rejectValue: MeteringError }
>('metering/fetchUsage', async ({ projectId, what }, { rejectWithValue }) => {
  try {
    return await meteringServiceHttp.usage(projectId, what)
  } catch (error) {
    return rejectWithValue(toMeteringError(error))
  }
}, {
  condition: ({ projectId, what }, { getState }) => {
    const { metering } = getState() as { metering: MeteringState }
    return shouldFetch(metering.caches.usage, usageKey(projectId, what))
  }
})

/** Load the run-budget remainder — a 404 is the no-budget fact, not a failure. */
export const fetchRunBudget = createAsyncThunk<
  RemainingBudget,
  string,
  { rejectValue: MeteringError }
>('metering/fetchRunBudget', async (projectId, { rejectWithValue }) => {
  try {
    return await meteringServiceHttp.runBudget(projectId)
  } catch (error) {
    return rejectWithValue(toMeteringError(error))
  }
}, {
  condition: (projectId, { getState }) => {
    const { metering } = getState() as { metering: MeteringState }
    return shouldFetch(metering.caches.remaining, projectId)
  }
})

// ---------------------------------------------------------------------------
// Slice
// ---------------------------------------------------------------------------

export const meteringSlice = createSlice({
  name: 'metering',
  initialState,
  reducers: {
    /** Dismiss a surfaced error and re-arm both caches (the Retry path). */
    meteringErrorDismissed(state) {
      state.error = null
      invalidate(state.caches.usage)
      invalidate(state.caches.remaining)
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMeteringUsage.pending, (state, action) => {
        if (state.caches.usage.loadedKey !== usageKey(action.meta.arg.projectId, action.meta.arg.what)) {
          state.usage = null
        }
        fetchStarted(state.caches.usage)
        state.error = null
      })
      .addCase(fetchMeteringUsage.fulfilled, (state, action) => {
        state.usage = action.payload
        state.usageShape = action.meta.arg.what
        fetchSucceeded(state.caches.usage, usageKey(action.meta.arg.projectId, action.meta.arg.what))
      })
      .addCase(fetchMeteringUsage.rejected, (state, action) => {
        fetchFailed(state.caches.usage)
        state.error = action.payload ?? null
      })
      .addCase(fetchRunBudget.pending, (state, action) => {
        if (state.caches.remaining.loadedKey !== action.meta.arg) {
          state.remaining = null
          state.remainingMissing = false
        }
        fetchStarted(state.caches.remaining)
        state.error = null
      })
      .addCase(fetchRunBudget.fulfilled, (state, action) => {
        state.remaining = action.payload
        state.remainingMissing = false
        fetchSucceeded(state.caches.remaining, action.meta.arg)
      })
      .addCase(fetchRunBudget.rejected, (state, action) => {
        fetchFailed(state.caches.remaining)
        if (action.payload?.status === 404) {
          // Absence is a fact, not an error (I-7).
          state.remaining = null
          state.remainingMissing = true
          state.error = null
        } else {
          state.error = action.payload ?? null
        }
      })
  }
})

export const { meteringErrorDismissed } = meteringSlice.actions
